// Copyright (c) 2025 Greener Power Solutions.
// This file is part of Greener Power Solutions' software,
// and may not be altered, copied, redistributed, or sold
// without specific permission.
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Avatar, ButtonBase, Divider, IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import useAuth from 'hooks/useAuth';
import { useSnackbar } from 'notistack';
import LanguageSelector from '../LanguageSelector';
import { ManagementSelection } from './ManagementSelection';

export function Account() {
  const navigate = useNavigate();
  const ref = useRef(null);
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [isOpen, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleLogout = () => {
    try {
      handleClose();
      logout();
      navigate('/');
    } catch (err) {
      console.error(err);
      enqueueSnackbar('Unable to logout', { variant: 'error' });
    }
  };

  return (
    <>
      <Tooltip title={t('navigation.account')}>
        <IconButton
          style={{ display: 'flex' }}
          component={ButtonBase}
          onClick={handleOpen}
          ref={ref}
          size="large"
          data-testid="user-account-button"
        >
          <Avatar alt="User" src={user?.avatar} sx={{ height: 24, width: 24 }} />
        </IconButton>
      </Tooltip>
      <Menu
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        keepMounted
        PaperProps={{ sx: { '& .MuiMenuItem-root': { minHeight: '40px', width: '200px' } } }}
        anchorEl={ref.current}
        open={isOpen}
      >
        <MenuItem sx={{ pointerEvents: 'none' }}>
          <Typography variant="h6" color="inherit">
            {user?.firstName}
          </Typography>
        </MenuItem>

        <Divider />

        <ManagementSelection onItemClick={handleClose} />

        <Divider />

        <LanguageSelector />
        <MenuItem onClick={handleClose} component={Link} to="/preferences">
          {t('navigation.userPreferences')}
        </MenuItem>
        <MenuItem onClick={handleClose} component={Link} to="/glossary">
          {t('navigation.glossary')}
        </MenuItem>
        <MenuItem onClick={handleLogout} data-testid="logout-button">
          {t('navigation.logout')}
        </MenuItem>
        {import.meta.env.MODE !== 'production' && (
          <>
            <Divider />
            <MenuItem onClick={handleClose} component={Link} to="/test-data">
              {t('navigation.testData')}
            </MenuItem>
            <Divider />
          </>
        )}
        <MenuItem disabled>
          <Typography variant="body1">
            {t('page.version', { version: import.meta.env.VITE_VERSION || 'Development' })}
          </Typography>
        </MenuItem>
      </Menu>
    </>
  );
}
